import type { VentilationRemunerationDTO } from '@/features/api/extranet/dto/Demande/CAPP'
import { type AdresseDTO, type EtablissementPrestataireDTO } from '@/features/api/extranet/dto/shared'
import { useAppStateStore } from '@/features/application'
import { getRemunerationProperty } from '@/features/domaine/regles-akto/CAPP/tools'
import { isAdherentOuMandatGestion } from '@/features/domaine/regles-akto/shared/tools'
import { deepEqual, getAge, getToday, isDate, isNullOrUndefined } from '@/features/shared/tools'
import { storeToRefs } from 'pinia'
import { computed, ref, watch } from 'vue'
import { buildDemandeActions } from '../shared'
import type { CappActions } from './DemandeCAPP.type'
import { useDemandeCAPPStore } from './useDemandeCAPPStore'

export function useDemandeCappActions(): CappActions {
  const store = useDemandeCAPPStore()
  const { model } = storeToRefs(store)
  const { setupCustomUpdater } = store
  const appState = useAppStateStore()

  const actionsDemande = buildDemandeActions({
    dispositif: 'CAPP',
    store: store as any,
  })

  const mineurDisabled = computed(() => {
    let age: number | null = null
    if (isDate(model.value?.apprenti?.dateNaissance) && isDate(model.value?.contrat?.dateConclusion)) {
      age = getAge(new Date(model.value.apprenti.dateNaissance!), new Date(model.value.contrat.dateConclusion))
      return age >= 18
    }

    if (isDate(model.value?.apprenti?.dateNaissance)) {
      age = getAge(new Date(model.value.apprenti.dateNaissance!), getToday())
      return age >= 18
    }

    return false
  })

  const representantLegalDisabled = computed(() => {
    let age: number | null = null
    if (isDate(model.value?.apprenti?.dateNaissance) && isDate(model.value?.contrat?.dateConclusion)) {
      age = getAge(new Date(model.value.apprenti.dateNaissance), new Date(model.value.contrat.dateConclusion))
      return age >= 18 || (age < 18 && model.value?.representantLegal?.mineurEmancipe === true)
    }

    if (isDate(model.value?.apprenti?.dateNaissance)) {
      age = getAge(new Date(model.value.apprenti.dateNaissance), getToday())
      return age >= 18 || (age < 18 && model.value?.representantLegal?.mineurEmancipe === true)
    }

    return model.value?.representantLegal?.mineurEmancipe === true
  })

  // Syncho Adresses apprenti et representant légal
  const estMemeAdresseInit =
    model.value?.representantLegal?.mineurEmancipe === false
      ? deepEqual(model.value?.apprenti?.adresse, model.value?.representantLegal?.adresse)
      : null
  const estMemeAddresseRepresentantLegal = ref<boolean | null>(estMemeAdresseInit)

  const updateAdresseRepresentantLegal = setupCustomUpdater<AdresseDTO>((patch, value) => {
    if (patch.representantLegal !== null) patch.representantLegal!.adresse = { ...value }
  })

  watch(
    () => ({
      adresseApprenti: { ...model.value?.apprenti?.adresse },
      mineurEmancipe: model.value?.representantLegal?.mineurEmancipe,
      memeAdresse: estMemeAddresseRepresentantLegal.value,
      dateNaissance: model.value?.apprenti?.dateNaissance,
    }),
    (newValue, oldValue) => {
      if (deepEqual(newValue, oldValue)) return
      if (
        estMemeAddresseRepresentantLegal.value === false ||
        isNullOrUndefined(estMemeAddresseRepresentantLegal.value)
      ) {
        if (
          deepEqual(model.value?.apprenti?.adresse, model.value?.representantLegal?.adresse) ||
          mineurDisabled.value === true
        ) {
          updateAdresseRepresentantLegal({} as AdresseDTO)
        }
        return
      }
      if (model.value?.representantLegal?.mineurEmancipe === true) return

      const adresseApprenti = model.value?.apprenti?.adresse

      if (!adresseApprenti) return
      if (deepEqual(adresseApprenti, model.value?.representantLegal?.adresse)) return
      updateAdresseRepresentantLegal(adresseApprenti)
    }
  )

  // CFA Lieu formation identique CFA Principal
  const updateCfaLieuFormation = setupCustomUpdater<EtablissementPrestataireDTO | null>((patch, value) => {
    if (value === null) patch.formation!.cfaEtablissementLieuPrestation = null
    else patch.formation!.cfaEtablissementLieuPrestation = { ...value }
  })

  watch(
    () => model.value?.formation?.lieuFormationIdentiqueCfaPrincipal,
    () => {
      updateCfaLieuFormation(null)
    },
  )

  const updateVentilation = setupCustomUpdater<void>((patch) => {
    const remunerationProperty = getRemunerationProperty(
      new Date(patch.apprenti!.dateNaissance!),
      new Date(patch.contrat!.dateDebutExecutionContrat!),
      new Date(patch.contrat!.dateFinContratOuPeriodeApprentissage!),
    )
    const ventilations: VentilationRemunerationDTO[] = []
    for (const [_index, prop] of remunerationProperty.entries()) {
      ventilations.push({ dateRange: prop.date } as any)
    }

    patch.remuneration!.ventilations!.forEach((_value, index) => {
      if (index < ventilations.length && ventilations[index].dateRange!.start)
        patch.remuneration!.ventilations!.at(index)!.dateRange = ventilations[index].dateRange
      else patch.remuneration!.ventilations!.at(index)!.dateRange = null
    })
  })

  watch(
    () => ({
      dateDebutExecutionContrat: model.value?.contrat?.dateDebutExecutionContrat,
      dateFinContratOuPeriodeApprentissage: model.value?.contrat?.dateFinContratOuPeriodeApprentissage,
      dateNaissance: model.value?.apprenti?.dateNaissance,
      readonly: store.readonly,
    }),
    (newValue, oldValue) => {
      if (newValue.readonly) return
      if (deepEqual(newValue, oldValue)) return

      if (
        isDate(model.value?.apprenti?.dateNaissance) &&
        isDate(model.value?.contrat?.dateDebutExecutionContrat) &&
        isDate(model.value?.contrat?.dateFinContratOuPeriodeApprentissage) &&
        isAdherentOuMandatGestion(appState.contextCourant, model.value!.estSousMandatGestion!)
      ) {
        // Preserve le statut est modifié pour les cas du immediate
        // ansi que le cas les transmitions prestataire qui entrainent
        // un changement de status de la demande
        store.preserveEstModifié(updateVentilation)
      }
    }, {
    immediate: true
  })

  const updateMontantMajorationTravailleurHandicape = setupCustomUpdater<number | null>((patch, montant) => {
    const frais = patch.formation!.fraisSupportesCfa!.find((f) => f.nature === 'MajorationTravailleurHandicape')!
    frais.montant = montant
    frais.quantite = null
  })

  watch(
    () => model.value?.apprenti?.beneficieReconnaissanceTravailleurHandicape,
    () => {
      if (model.value?.apprenti?.beneficieReconnaissanceTravailleurHandicape !== true)
        updateMontantMajorationTravailleurHandicape(null)
    },
  )

  return {
    ...actionsDemande,
    representantLegalDisabled,
    estMemeAddresseRepresentantLegal,
    mineurDisabled,
  }
}
